import {
	ServicePointsResponseType,
	DynamicEndpointQuery,
	GenericReferential,
	PriceIndex,
	ClickTypesResponseType,
	GetContactsResponseType,
	GetBCVConfigResponseType,
	BookType,
	FieldDataDefQuery,
	PricingAddons,
} from "./types";
import contractManagementGecoApi from "../contractManagementGecoApi";

const service = contractManagementGecoApi.injectEndpoints({
	endpoints: (builder) => ({
		getCertificateTypes: builder.query<
			GenericReferential[],
			DynamicEndpointQuery
		>({
			query: ({ endpoint }) => ({
				url: `${endpoint || ""}/referentials/certificate_types`,
				method: "GET",
			}),
		}),
		getClickPeriods: builder.query<
			ClickTypesResponseType[],
			DynamicEndpointQuery
		>({
			query: ({ endpoint }) => ({
				url: `${endpoint || ""}/referentials/click_periods`,
				method: "GET",
			}),
		}),
		getContractTypes: builder.query<
			GenericReferential[],
			DynamicEndpointQuery
		>({
			query: ({ endpoint }) => ({
				url: `${endpoint || ""}/referentials/contract_types`,
				method: "GET",
			}),
		}),
		getNegativePriceIndexes: builder.query<
			PriceIndex[],
			{ country_code: string } & DynamicEndpointQuery
		>({
			query: ({ country_code, endpoint }) => {
				return {
					url: `${endpoint || ""}/referentials/indexes`,
					method: "GET",
					params: {
						country_code,
					},
				};
			},
		}),
		getTsos: builder.query<GenericReferential[], DynamicEndpointQuery>({
			query: ({ endpoint }) => {
				return {
					url: `${endpoint || ""}/referentials/tsos`,
					method: "GET",
				};
			},
		}),
		getDsos: builder.query<GenericReferential[], DynamicEndpointQuery>({
			query: ({ endpoint }) => {
				return {
					url: `${endpoint || ""}/referentials/dsos`,
					method: "GET",
				};
			},
		}),
		getEicBalancingGroups: builder.query<
			GenericReferential[],
			DynamicEndpointQuery
		>({
			query: ({ endpoint }) => {
				return {
					url: `${endpoint || ""}/referentials/eic_balancing_groups`,
					method: "GET",
				};
			},
		}),
		getServicePoints: builder.query<
			ServicePointsResponseType,
			{ search__name: string } & DynamicEndpointQuery
		>({
			query: ({ endpoint, ...params }) => {
				return {
					url: `${endpoint || ""}/referentials/servicepoints`,
					method: "GET",
					params,
				};
			},
		}),
		getServicePointTypes: builder.query<
			GenericReferential[],
			DynamicEndpointQuery
		>({
			query: ({ endpoint }) => {
				return {
					url: `${endpoint || ""}/referentials/servicepoint_types`,
					method: "GET",
				};
			},
		}),
		getServicePointCodeTypes: builder.query<
			GenericReferential[],
			DynamicEndpointQuery
		>({
			query: ({ endpoint }) => {
				return {
					url: `${
						endpoint || ""
					}/referentials/servicepoint_code_types`,
					method: "GET",
				};
			},
		}),
		getBooks: builder.query<BookType[], DynamicEndpointQuery>({
			query: ({ endpoint }) => {
				return {
					url: `${endpoint || ""}/referentials/books`,
					method: "GET",
				};
			},
		}),
		getCountries: builder.query<GenericReferential[], DynamicEndpointQuery>(
			{
				query: ({ endpoint }) => {
					return {
						url: `${endpoint || ""}/referentials/countries`,
						method: "GET",
					};
				},
			}
		),
		getAgreementTypes: builder.query<
			GenericReferential[],
			DynamicEndpointQuery
		>({
			query: ({ endpoint }) => {
				return {
					url: `${endpoint || ""}/referentials/agreement_types`,
					method: "GET",
				};
			},
		}),
		getMachines: builder.query<GenericReferential[], DynamicEndpointQuery>({
			query: ({ endpoint }) => {
				return {
					url: `${
						endpoint || ""
					}/referentials/machines?page=1&per_page=500&sort=name`,
					method: "GET",
				};
			},
		}),
		getContacts: builder.query<
			GetContactsResponseType,
			{
				search__name?: string;
				search__email?: string;
			} & DynamicEndpointQuery
		>({
			query: ({ search__name, search__email, endpoint }) => {
				return {
					url: `${endpoint || ""}/referentials/contacts`,
					method: "GET",
					params: {
						search__name,
						search__email,
					},
				};
			},
		}),
		getBCVConfig: builder.query<
			GetBCVConfigResponseType[],
			{
				search__name?: string;
			} & DynamicEndpointQuery
		>({
			query: ({ search__name, endpoint }) => {
				return {
					url: `${endpoint || ""}/referentials/bcv_config`,
					method: "GET",
					params: {
						search__name,
					},
				};
			},
		}),
		getTechnicalResourceOperator: builder.query<
			GenericReferential[],
			DynamicEndpointQuery
		>({
			query: ({ endpoint }) => {
				return {
					url: `${
						endpoint || ""
					}/referentials/technical_resource_operators`,
					method: "GET",
				};
			},
		}),
		getFielddataDefinitions: builder.query<
			FieldDataDefQuery[],
			DynamicEndpointQuery
		>({
			query: ({ endpoint }) => {
				return {
					url: `${endpoint || ""}/referentials/fielddata_definitions`,
					method: "GET",
				};
			},
		}),
		getPricingAddons: builder.query<PricingAddons[], DynamicEndpointQuery>({
			query: ({ endpoint }) => {
				return {
					url: `${endpoint || ""}/referentials/pricing_addons`,
					method: "GET",
				};
			},
		}),
	}),
});

export const {
	useGetAgreementTypesQuery,
	useGetBooksQuery,
	useGetClickPeriodsQuery,
	useGetContactsQuery,
	useLazyGetContactsQuery,
	useGetContractTypesQuery,
	useGetCountriesQuery,
	useGetDsosQuery,
	useGetEicBalancingGroupsQuery,
	useGetMachinesQuery,
	useGetNegativePriceIndexesQuery,
	useGetServicePointCodeTypesQuery,
	useGetServicePointTypesQuery,
	useGetTsosQuery,
	useGetBCVConfigQuery,
	useLazyGetBCVConfigQuery,
	useLazyGetNegativePriceIndexesQuery,
	useLazyGetServicePointsQuery,
	useGetTechnicalResourceOperatorQuery,
	useGetFielddataDefinitionsQuery,
	useGetPricingAddonsQuery,
} = service;

export default service;
